<template>
  <div class="m-section">
    <div class="m-section__header">
      <div class="row justify-content-center align-items-center">
        <div class="col-md-5 col-12">
          <div class="m-section__headline mb-2">
            Tu progreso On Demand
            <span class="m-section__date ml-2" v-if="readableRange">{{ readableRange }}</span>
          </div>
        </div>
        <div class="col-md-7 col-12">
          <div class="row justify-content-end align-items-center">
            <div class="col-md-auto pt-3">
              <a class="link-download" @click="goToLevels">Ver detalle en Niveles</a>
            </div>
            <div class="col-md-auto pt-3" v-if="!hasFailed">
              <div class="m-section__filter-date">
                Periodo:
                <span class="ml-1">
                  <select v-model="selectedRange" class="form-control w-auto">
                    <option :value="FORTNIGHT_RANGE">Última quincena</option>
                    <option :value="LAST_MONTH_RANGE">Últimos 30 días</option>
                  </select>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <progress-state
      v-if="hasFailed"
      variant="failed"
      title="No pudimos cargar tu progreso"
      text="Fue un problema temporal al consultar tus niveles."
      :is-retrying="isRetrying"
      @retry="load"
    />

    <vue-content-loading
      v-else-if="isLoadingDays"
      :width="300"
      :height="120"
      primary="#bdc3c7"
      secondary="#95a5a6"
    >
      <svg:rect x="0" y="0" rx="3" ry="3" width="120" height="10" />
      <svg:rect x="0" y="20" rx="3" ry="3" width="300" height="90" />
    </vue-content-loading>

    <progress-state
      v-else-if="hasNoDays"
      variant="empty"
      title="Sin días calculados"
      text="Todavía no hay días calculados en este periodo. Cuando el motor de niveles procese tu operación, vas a ver la evolución acá."
    />

    <template v-else>
      <div class="m-section__tabs">
        <div class="">
          <tabs :fill="false" circle>
            <tab-pane :title="LEVEL_TAB_TITLE">
              <span slot="title" class="nav-link-icon d-block">{{ LEVEL_TAB_TITLE }}</span>
              <div class="tab-h tab-h--expandable">
                <div class="m-card__header tab-header">
                  <div class="title-c">{{ LEVEL_TAB_TITLE }}</div>
                  <CardArrowLink :to="LEVELS_ROUTE" />
                </div>
                <line-chart
                  :height="350"
                  :chart-data="levelChartData"
                  :extra-options="chartOptions"
                />
              </div>
            </tab-pane>

            <tab-pane v-for="metric in metrics" :key="metric.key" :title="metric.title">
              <span slot="title" class="nav-link-icon d-block">{{ metric.title }}</span>
              <div class="tab-h">
                <div class="m-card__header tab-header">
                  <div class="title-c">{{ metric.title }}</div>
                  <CardArrowLink :to="LEVELS_ROUTE" />
                </div>
                <line-chart
                  :height="350"
                  :chart-data="chartDataFor(metric)"
                  :extra-options="chartOptions"
                />
                <div class="coverage">
                  {{ metric.title }}: {{ seriesFor(metric.key).measured }} de
                  {{ seriesFor(metric.key).total }} días medidos
                  <span v-if="metric.note"> · {{ metric.note }}</span>
                </div>
              </div>
            </tab-pane>
          </tabs>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import { VueContentLoading } from 'vue-content-loading'
import Tabs from '@/components/Tabs/Tabs.vue'
import TabPane from '@/components/Tabs/TabPane.vue'
import LineChart from '@/components/Charts/LineChart'
import { levelDaysChartOptions } from '@/components/Charts/config'
import ProgressState from './OnDemandProgress/ProgressState'
import CardArrowLink from './CardArrowLink'
import { FORTNIGHT_RANGE, LAST_MONTH_RANGE } from '@/store/modules/foodcourtLevels'

const LEVEL_TAB_TITLE = 'Nivel diario'
const ISO_DATE_FORMAT = 'YYYY-MM-DD'
const DAY_MONTH_FORMAT = 'DD/MM'
const LEVEL_LINE_COLOR = '#4D3899'
const LEVEL_POINT_COLOR = '#35B266'
const METRIC_LINE_COLOR = '#27884D'
const LEVELS_ROUTE = '/foodcourt/niveles'

const METRICS = [
  { key: 'incidents', title: 'Gravedad por pedido', note: 'los días sin pedidos no se miden' },
  { key: 'availability', title: 'Disponibilidad de horario', note: 'los domingos no se registran' },
  { key: 'active_menu', title: 'Carta activa', note: 'los domingos no se registran' }
]

export default {
  name: 'OnDemandProgress',
  components: { VueContentLoading, Tabs, TabPane, LineChart, ProgressState, CardArrowLink },
  data () {
    return {
      FORTNIGHT_RANGE,
      LAST_MONTH_RANGE,
      LEVEL_TAB_TITLE,
      LEVELS_ROUTE,
      metrics: METRICS,
      hasFailed: false,
      isRetrying: false,
      chartOptions: levelDaysChartOptions
    }
  },
  async created () {
    await this.load()
  },
  computed: {
    ...mapGetters('foodcourtLevels', [
      'isLoadingDays',
      'hasNoDays',
      'getDayLabels',
      'getDaysRange',
      'getMetricSeries',
      'getLevelSeries',
      'getSelectedDaysRange'
    ]),

    levelSeries () {
      return this.getLevelSeries
    },

    dayLabels () {
      return this.getDayLabels.map(date => moment(date, ISO_DATE_FORMAT).format(DAY_MONTH_FORMAT))
    },

    selectedRange: {
      get () {
        return this.getSelectedDaysRange
      },
      set (range) {
        this.selectRange(range)
      }
    },

    readableRange () {
      const range = this.getDaysRange
      if (!range.from || !range.to) return ''
      return (
        'Del ' +
        moment(range.from, ISO_DATE_FORMAT).format(DAY_MONTH_FORMAT) +
        ' al ' +
        moment(range.to, ISO_DATE_FORMAT).format(DAY_MONTH_FORMAT)
      )
    },

    levelChartData () {
      return this.buildChartData('Nivel', this.levelSeries.values, LEVEL_LINE_COLOR, LEVEL_POINT_COLOR)
    }
  },
  methods: {
    ...mapActions('foodcourtLevels', ['SELECT_DAYS_RANGE']),

    seriesFor (metricKey) {
      return this.getMetricSeries(metricKey)
    },

    buildChartData (label, values, lineColor, pointColor) {
      return {
        labels: this.dayLabels,
        datasets: [
          {
            label: label,
            data: values,
            borderColor: lineColor,
            borderWidth: 2,
            pointBackgroundColor: pointColor,
            pointBorderColor: pointColor,
            fill: false
          }
        ]
      }
    },

    chartDataFor (metric) {
      return this.buildChartData(
        metric.title,
        this.seriesFor(metric.key).values,
        METRIC_LINE_COLOR,
        METRIC_LINE_COLOR
      )
    },

    async selectRange (range) {
      this.isRetrying = true
      this.hasFailed = false
      try {
        await this.SELECT_DAYS_RANGE(range)
      } catch (error) {
        this.hasFailed = true
      } finally {
        this.isRetrying = false
      }
    },

    async load () {
      await this.selectRange(this.getSelectedDaysRange)
    },

    goToLevels () {
      this.$router.push(LEVELS_ROUTE)
    }
  }
}
</script>

<style lang="scss" scoped>
.nav-link {
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  color: #2d2d2d !important;
}

.coverage {
  font-size: 12px;
  color: #8898aa;
  padding: 10px 0 4px;
}

.tab-header {
  border-bottom: none;
  padding: 0 0 8px;
}

::v-deep .tab-h--expandable {
  height: auto !important;
}
</style>
